/**
 * 地区 GIS レイヤーの購読 + 解決 hook (docs/22 要件4/5、Sprint G3/G4)。
 *
 * gisLayers (admin 既定) を購読し、ユーザー個別上書き (localStorage) を merge して:
 *   - `overlays`: 表示 ON のレイヤーを実効スタイル + GeoJSON 付きで返す (MapView 重畳用、要件4)
 *   - `items`   : 全レイヤーを 表示/順序/スタイル 実効値付きで返す (GIS設定 UI 用、要件5)
 *   - setVisible / move / setStyle / resetLayer / resetAll: ユーザー上書きの更新 (localStorage 永続)
 *
 * GeoJSON 本体は Storage から getBytes で取得し storagePath でメモリキャッシュする。
 */

import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { getBytes, ref as storageRef } from 'firebase/storage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  resolveGisLayerStyle,
  type GisLayer,
  type GisLayerStyle,
  type GisLayerUserOverride,
} from '@citizen-walk/shared-types';
import { db, storage } from '../../lib/firebase';
import type { GisOverlayLayer } from './MapView';
import { getOfflineGisEntry, GIS_OFFLINE_EVENT, type OfflineGisEntry } from './offlineGis';

type OverrideMap = Record<string, GisLayerUserOverride>;

/** 同一タブ内の別 hook インスタンス間で上書き変更を伝播するイベント (GIS設定 modal → 地図 overlay)。 */
const GIS_OVERRIDES_EVENT = 'cw-gis-overrides-changed';

function overridesKey(districtId: string): string {
  return `cw.gis.overrides.v1.${districtId}`;
}

function loadOverrides(districtId: string): OverrideMap {
  try {
    const raw = localStorage.getItem(overridesKey(districtId));
    return raw ? (JSON.parse(raw) as OverrideMap) : {};
  } catch {
    return {};
  }
}

function saveOverrides(districtId: string, overrides: OverrideMap): void {
  try {
    localStorage.setItem(overridesKey(districtId), JSON.stringify(overrides));
    // 同一タブの他インスタンス (GIS設定 modal ↔ 地図 overlay) へ即時反映 (storage event は他タブのみ)。
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(GIS_OVERRIDES_EVENT, { detail: { districtId } }));
    }
  } catch {
    /* localStorage 不可 (プライベートモード等) は無視 */
  }
}

export interface GisLayerItem {
  layer: GisLayer;
  visible: boolean;
  order: number;
  style: GisLayerStyle;
}

export interface UseDistrictGisLayersResult {
  /** 表示 ON のレイヤーを実効スタイル + GeoJSON 付きで (昇順、MapView 重畳用)。 */
  overlays: GisOverlayLayer[];
  /** 全レイヤーの実効 表示/順序/スタイル (GIS設定 UI 用)。 */
  items: GisLayerItem[];
  hasLayers: boolean;
  loading: boolean;
  setVisible: (layerId: string, visible: boolean) => void;
  move: (layerId: string, dir: -1 | 1) => void;
  setStyle: (layerId: string, style: Partial<GisLayerStyle>) => void;
  resetLayer: (layerId: string) => void;
  resetAll: () => void;
}

export function useDistrictGisLayers(
  districtId: string | null | undefined,
  enabled: boolean,
  options?: { buildOverlays?: boolean },
): UseDistrictGisLayersResult {
  // GIS設定 modal は items のみ使うため buildOverlays=false で GeoJSON 取得を省略できる。
  const buildOverlays = options?.buildOverlays ?? true;
  const [rawLayers, setRawLayers] = useState<GisLayer[]>([]);
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [overlays, setOverlays] = useState<GisOverlayLayer[]>([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<Map<string, GeoJSON.FeatureCollection>>(new Map());
  // docs/98 R-50: オフライン DL 済 GIS データ (IndexedDB)。Firestore/Storage がオフラインで使えない
  // ときの fallback ソース。明示 DL されていれば overlay を即時 + ネット非依存で再構成できる。
  const [offlineEntry, setOfflineEntry] = useState<OfflineGisEntry | null>(null);

  // 地区切替で上書きをロード
  useEffect(() => {
    setOverrides(districtId ? loadOverrides(districtId) : {});
  }, [districtId]);

  // 他インスタンス / 他タブの上書き変更を購読して再ロード (modal で変更 → 地図 overlay に即時反映)。
  useEffect(() => {
    if (!districtId) return;
    const reload = () => setOverrides(loadOverrides(districtId));
    const onCustom = (e: Event) => {
      const d = (e as CustomEvent<{ districtId?: string }>).detail?.districtId;
      if (!d || d === districtId) reload();
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === overridesKey(districtId)) reload();
    };
    window.addEventListener(GIS_OVERRIDES_EVENT, onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(GIS_OVERRIDES_EVENT, onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [districtId]);

  // オフライン GIS cache のロード + 変更購読 (MapDownloadPage の DL / 削除を地図 overlay に即時反映)。
  useEffect(() => {
    if (!districtId || !enabled) {
      setOfflineEntry(null);
      return;
    }
    let cancelled = false;
    const load = () => {
      void getOfflineGisEntry(districtId)
        .then((e) => {
          if (!cancelled) setOfflineEntry(e ?? null);
        })
        .catch(() => {
          if (!cancelled) setOfflineEntry(null);
        });
    };
    load();
    const onChanged = (ev: Event) => {
      const d = (ev as CustomEvent<{ districtId?: string }>).detail?.districtId;
      if (!d || d === districtId) load();
    };
    window.addEventListener(GIS_OFFLINE_EVENT, onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener(GIS_OFFLINE_EVENT, onChanged);
    };
  }, [districtId, enabled]);

  // gisLayers 購読 (enabled のときのみ)
  useEffect(() => {
    if (!districtId || !enabled) {
      setRawLayers([]);
      return;
    }
    const q = query(collection(db, 'districts', districtId, 'gisLayers'), orderBy('displayOrder'));
    const unsub = onSnapshot(
      q,
      (snap) =>
        setRawLayers(snap.docs.map((d) => ({ ...(d.data() as Omit<GisLayer, 'id'>), id: d.id }))),
      () => setRawLayers([]),
    );
    return () => unsub();
  }, [districtId, enabled]);

  // メタの実効ソース: Firestore が配れていれば rawLayers、空 (オフライン / cache 未配信) なら
  // offline DL cache に保存したメタ snapshot を使う。これでネット非依存でレイヤー一覧を再構成できる。
  const sourceLayers = useMemo<GisLayer[]>(
    () => (rawLayers.length > 0 ? rawLayers : (offlineEntry?.layers.map((l) => l.meta) ?? [])),
    [rawLayers, offlineEntry],
  );

  // storagePath → GeoJSON (offline DL cache をパースしたもの)。overlay 取得の最優先ソース。
  const offlineGeo = useMemo<Map<string, GeoJSON.FeatureCollection>>(() => {
    const m = new Map<string, GeoJSON.FeatureCollection>();
    for (const l of offlineEntry?.layers ?? []) {
      try {
        m.set(l.meta.storagePath, JSON.parse(l.geojson) as GeoJSON.FeatureCollection);
      } catch {
        /* 壊れた cache entry は無視 */
      }
    }
    return m;
  }, [offlineEntry]);

  // 実効値 (visible/order/style) を算出 (admin 既定 + ユーザー上書き)
  const items = useMemo<GisLayerItem[]>(() => {
    return sourceLayers
      .map((layer) => {
        const ov = overrides[layer.id];
        return {
          layer,
          visible: ov?.visible ?? layer.defaultVisible,
          order: ov?.order ?? layer.displayOrder,
          style: resolveGisLayerStyle(layer.style, ov?.style),
        };
      })
      .sort((a, b) => a.order - b.order || a.layer.id.localeCompare(b.layer.id));
  }, [sourceLayers, overrides]);

  // 表示 ON レイヤーの GeoJSON を取得して overlays 化
  const visibleSig = useMemo(
    () =>
      JSON.stringify(
        items
          .filter((e) => e.visible)
          .map((e) => ({
            id: e.layer.id,
            sp: e.layer.storagePath,
            gt: e.layer.geometryType,
            s: e.style,
            n: e.layer.name,
          })),
      ),
    [items],
  );

  useEffect(() => {
    let cancelled = false;
    const visible = items.filter((e) => e.visible);
    if (!enabled || !buildOverlays || visible.length === 0) {
      setOverlays([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      const built: GisOverlayLayer[] = [];
      for (const e of visible) {
        // 解決順: メモリ cache → オフライン DL cache (明示 DL 優先 = オフライン安全 + 即時) →
        //          Storage getBytes (オンライン) → 取得失敗時は再度オフライン cache → 空。
        let fc = cacheRef.current.get(e.layer.storagePath) ?? offlineGeo.get(e.layer.storagePath);
        if (!fc) {
          try {
            const bytes = await getBytes(storageRef(storage, e.layer.storagePath));
            fc = JSON.parse(new TextDecoder().decode(bytes)) as GeoJSON.FeatureCollection;
            cacheRef.current.set(e.layer.storagePath, fc);
          } catch {
            fc = offlineGeo.get(e.layer.storagePath) ?? { type: 'FeatureCollection', features: [] };
          }
        }
        if (cancelled) return;
        built.push({
          id: e.layer.id,
          name: e.layer.name,
          geometryType: e.layer.geometryType,
          style: e.style,
          data: fc,
        });
      }
      if (!cancelled) {
        setOverlays(built);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleSig, enabled, buildOverlays, offlineGeo]);

  // ── ユーザー上書き setter (localStorage 永続、関数型更新で concurrent-safe) ──────────────
  // setOverrides の updater 内で saveOverrides を呼ぶ (prev を確実に基にするため)。
  // 変化なし (next === prev) のときは write/event をスキップ。
  const writeOverrides = useCallback(
    (updater: (prev: OverrideMap) => OverrideMap) => {
      setOverrides((prev) => {
        const next = updater(prev);
        if (next === prev) return prev;
        if (districtId) saveOverrides(districtId, next);
        return next;
      });
    },
    [districtId],
  );
  const setVisible = useCallback(
    (layerId: string, visible: boolean) => {
      writeOverrides((prev) => ({
        ...prev,
        [layerId]: { ...(prev[layerId] ?? {}), layerId, visible },
      }));
    },
    [writeOverrides],
  );
  const setStyle = useCallback(
    (layerId: string, style: Partial<GisLayerStyle>) => {
      writeOverrides((prev) => {
        const p = prev[layerId];
        return {
          ...prev,
          [layerId]: { ...(p ?? {}), layerId, style: { ...(p?.style ?? {}), ...style } },
        };
      });
    },
    [writeOverrides],
  );
  const move = useCallback(
    (layerId: string, dir: -1 | 1) => {
      writeOverrides((prev) => {
        const sorted = sourceLayers
          .map((l) => ({ id: l.id, order: prev[l.id]?.order ?? l.displayOrder }))
          .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
        const idx = sorted.findIndex((e) => e.id === layerId);
        const swap = idx + dir;
        if (idx < 0 || swap < 0 || swap >= sorted.length) return prev;
        const tmp = sorted[idx]!;
        sorted[idx] = sorted[swap]!;
        sorted[swap] = tmp;
        const next: OverrideMap = { ...prev };
        sorted.forEach((e, i) => {
          next[e.id] = { ...(next[e.id] ?? {}), layerId: e.id, order: i };
        });
        return next;
      });
    },
    [writeOverrides, sourceLayers],
  );
  const resetLayer = useCallback(
    (layerId: string) => {
      writeOverrides((prev) => {
        if (!prev[layerId]) return prev;
        const next = { ...prev };
        delete next[layerId];
        return next;
      });
    },
    [writeOverrides],
  );
  const resetAll = useCallback(() => {
    writeOverrides(() => ({}));
  }, [writeOverrides]);

  return {
    overlays,
    items,
    hasLayers: sourceLayers.length > 0,
    loading,
    setVisible,
    move,
    setStyle,
    resetLayer,
    resetAll,
  };
}
